/**
 * Утилита для расчета коэффициента на основе DampingCoefficient
 * Логика соответствует server/api/controllers/card-point-data/complete.js
 */

/**
 * Вычисляет коэффициент на основе DampingCoefficient, ContentViewPlan и CardPointData
 * @param {Object} params - Параметры для расчета
 * @param {Object} params.contentCardDefault - ContentCardDefault с типом (photo/video/case)
 * @param {Object} params.cardPointData - CardPointData с views
 * @param {Object} params.contentViewPlan - ContentViewPlan для текущего месяца/года
 * @param {Array} params.dampingCoefficients - Массив DampingCoefficient
 * @returns {number} - Рассчитанный коэффициент (по умолчанию 1)
 */
export const calculateCoefficient = ({
  contentCardDefault,
  cardPointData,
  contentViewPlan,
  dampingCoefficients,
}) => {
  // Если нет необходимых данных, возвращаем коэффициент по умолчанию
  if (!contentCardDefault || !cardPointData || !dampingCoefficients || dampingCoefficients.length === 0) {
    return 1;
  }

  // Вычисляем viewsBase на основе типа
  let viewsBase = 0;
  if (contentViewPlan) {
    const type = contentCardDefault.type;
    if (type === 'photo') {
      viewsBase = contentViewPlan.photo ?? 0;
    } else if (type === 'video') {
      viewsBase = contentViewPlan.video ?? 0;
    } else if (type === 'case') {
      viewsBase = contentViewPlan.caseCount ?? 0;
    }
  }

  const viewsReal = cardPointData.views ?? 0;
  const viewRange = viewsReal - viewsBase;

  // Константа для представления бесконечности (соответствует INFINITY_VALUE на фронтенде)
  const INFINITY_VALUE = 1000000000;
  const isInfinity = (value) => value === null || value === INFINITY_VALUE;

  // Default coefficient per spec.
  let realCoef = 1;

  let matchingCoefficient = null;

  // from/to в DampingCoefficient трактуются как проценты (percent может быть отрицательным)
  // Плановое значение соответствует 0%:
  // percent = ((viewsReal - viewsBase) / viewsBase) * 100
  let percent;
  if (viewsBase <= 0) {
    // Special-case to avoid division by zero
    percent = viewsReal === 0 ? 0 : Infinity;
  } else {
    percent = (viewRange / viewsBase) * 100;
  }

  // Ищем запись, где диапазон [from, to] покрывает percent
  // null в from означает "от -100%" (минимальное значение), INFINITY_VALUE в from означает "от -бесконечности"
  // null или INFINITY_VALUE в to означает "до +бесконечности"
  for (const dc of dampingCoefficients) {
    const from = dc.from;
    const to = dc.to;

    // Если from = INFINITY_VALUE, это "от -бесконечности" (matchesFrom = true для любого percent)
    // Если from = null, используем -100 (минимальное значение по документации)
    // Иначе: percent >= from
    const effectiveFrom = from === INFINITY_VALUE ? -Infinity : (from !== null ? from : -100);
    const matchesFrom = from === INFINITY_VALUE ? true : percent >= effectiveFrom;
    const matchesTo = isInfinity(to) || percent <= to;

    if (matchesFrom && matchesTo) {
      matchingCoefficient = dc;
      break; // Используем первую подходящую запись
    }
  }

  if (matchingCoefficient) {
    realCoef = matchingCoefficient.value ?? 1;
  }

  return realCoef;
};
